import { router } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AppHeader } from '@/components/app-header'
import {
  Users,
  UserCheck,
  Activity,
  Shield,
  UsersRound,
  Calendar,
  ClipboardList,
  AlertCircle,
  FileCheck,
  Stethoscope,
  BotIcon,
  Code,
  FileText,
  Brain,
  ChevronRight,
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertTriangle,
} from 'lucide-react'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts'

interface Stats {
  // User stats
  total_users: number
  admins: number
  regular_users: number
  active_sessions: number

  // Patient stats
  total_patients: number
  new_patients_today: number
  new_patients_this_week: number

  // Care case stats
  active_care_cases: number
  patients_cured: number

  // Appointment stats
  appointments_today: number
  appointments_this_week: number
  total_appointments_upcoming: number

  // Lab order stats
  pending_lab_orders: number

  // Follow-up stats
  overdue_follow_ups: number
}

interface PipelineDistribution {
  status: string
  count: number
}

interface RecentActivity {
  type: string
  action: string
  user_id: number | null
  created_at: string
  changes: Record<string, any>
}

interface AdminConsoleProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
    }
  }
  stats: Stats
  pipeline_distribution: PipelineDistribution[]
  recent_activities: RecentActivity[]
}

export default function AdminConsole({ auth, stats, pipeline_distribution, recent_activities }: AdminConsoleProps) {
  // Format pipeline data for chart
  const pipelineChartData = pipeline_distribution.map(item => ({
    stage: item.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    count: item.count,
  }))

  const chartConfig = {
    count: {
      label: 'Cases',
      color: 'hsl(var(--primary))',
    },
  }

  // Admin section cards configuration
  const adminSections = [
    {
      title: 'User Management',
      description: 'Manage staff users, roles, and invitations',
      icon: Users,
      url: '/admin/users',
      gradient: 'from-blue-500 to-indigo-600',
    },
    {
      title: 'Patient Management',
      description: 'View all patients across sites',
      icon: UsersRound,
      url: '/admin/patients',
      gradient: 'from-purple-500 to-pink-600',
    },
    {
      title: 'Appointments',
      description: 'View all scheduled appointments system-wide',
      icon: Calendar,
      url: '/admin/appointments',
      gradient: 'from-green-500 to-emerald-600',
    },
    {
      title: 'ICD-10 Codes',
      description: 'Manage diagnosis code library',
      icon: Code,
      url: '/admin/icd10_codes',
      gradient: 'from-orange-500 to-red-600',
    },
    {
      title: 'Smart Phrases',
      description: 'Manage documentation templates',
      icon: FileText,
      url: '/admin/smart_phrases',
      gradient: 'from-cyan-500 to-blue-600',
    },
    {
      title: 'AI Settings',
      description: 'Configure AI chatbot and knowledge base',
      icon: BotIcon,
      url: '/admin/ai_settings',
      gradient: 'from-violet-500 to-purple-600',
    },
    {
      title: 'Audit Logs',
      description: 'Review HIPAA compliance trail',
      icon: Shield,
      url: '/admin/audit_logs',
      gradient: 'from-slate-500 to-gray-600',
    },
  ]

  return (
    <>
      <AppHeader
        breadcrumbs={[{ label: 'Admin Console' }]}
        actions={
          <Button size="sm" onClick={() => router.visit('/admin/users')}>
            Manage Users
          </Button>
        }
      />

      <div className="flex flex-1 flex-col gap-6 p-6">
        {/* System Health Overview */}
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            System Health Overview
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Patients</CardTitle>
                <UsersRound className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{stats.total_patients}</div>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  +{stats.new_patients_this_week} this week
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border-purple-200 dark:border-purple-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Care Cases</CardTitle>
                <Stethoscope className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">{stats.active_care_cases}</div>
                <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                  {stats.patients_cured} patients cured
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Appointments</CardTitle>
                <Calendar className="h-4 w-4 text-green-600 dark:text-green-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">{stats.appointments_today}</div>
                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                  Today • {stats.appointments_this_week} this week
                </p>
              </CardContent>
            </Card>

            <Card className={`bg-gradient-to-br ${
              stats.overdue_follow_ups > 0
                ? 'from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border-red-200 dark:border-red-800'
                : 'from-slate-50 to-gray-50 dark:from-slate-950/20 dark:to-gray-950/20 border-slate-200 dark:border-slate-800'
            }`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Overdue Follow-Ups</CardTitle>
                {stats.overdue_follow_ups > 0 ? (
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                )}
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${
                  stats.overdue_follow_ups > 0
                    ? 'text-red-900 dark:text-red-100'
                    : 'text-slate-900 dark:text-slate-100'
                }`}>
                  {stats.overdue_follow_ups}
                </div>
                <p className={`text-xs mt-1 ${
                  stats.overdue_follow_ups > 0
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-slate-700 dark:text-slate-300'
                }`}>
                  {stats.overdue_follow_ups > 0 ? 'Requires attention' : 'All up to date'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* User Activity & Lab Stats */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Staff Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Users</span>
                  <span className="font-semibold">{stats.total_users}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Admins</span>
                  <Badge variant="secondary">{stats.admins}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Regular Users</span>
                  <span className="font-medium">{stats.regular_users}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="text-sm text-muted-foreground">Active Sessions</span>
                  <Badge variant="outline">{stats.active_sessions}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                New Registrations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-muted-foreground">Today</span>
                    <span className="text-2xl font-bold text-primary">{stats.new_patients_today}</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-muted-foreground">This Week</span>
                    <span className="text-xl font-semibold">{stats.new_patients_this_week}</span>
                  </div>
                </div>
                <div className="pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => router.visit('/admin/patients')}
                  >
                    View All Patients
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                Lab Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Pending Results</div>
                  <div className="text-3xl font-bold">
                    {stats.pending_lab_orders}
                  </div>
                </div>
                {stats.pending_lab_orders > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                    <Clock className="h-3 w-3" />
                    <span>Awaiting results</span>
                  </div>
                )}
                <div className="pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => router.visit('/admin/patients')}
                  >
                    Review Lab Orders
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Care Pipeline Distribution Chart */}
        {pipelineChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Care Pipeline Distribution
              </CardTitle>
              <CardDescription>
                Active patients across care stages
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipelineChartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="stage"
                      angle={-45}
                      textAnchor="end"
                      height={120}
                      className="text-xs"
                    />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey="count"
                      fill="hsl(var(--primary))"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        )}

        {/* Administrative Sections Quick Access */}
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Administrative Sections
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {adminSections.map((section) => {
              const IconComponent = section.icon
              return (
                <Card
                  key={section.url}
                  className="cursor-pointer transition-all hover:shadow-lg hover:scale-105"
                  onClick={() => router.visit(section.url)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className={`p-3 rounded-lg bg-gradient-to-br ${section.gradient}`}>
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardTitle className="text-base mb-2">{section.title}</CardTitle>
                    <CardDescription className="text-sm">
                      {section.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}
